'use client';

import type { ReactNode } from 'react';
import { Link } from '@/i18n/routing';
import type { ProgramActivity, ProgramActivityText } from './types';
import { getProgramActivityStatus, getProgramActivityTimeRange } from './utils';

type ProgramActivityCardProps = {
  activity: ProgramActivity;
  actions?: ReactNode;
  detailHref: string;
  text: ProgramActivityText;
  title: string;
};

export function ProgramActivityCard({ activity, actions, detailHref, text, title }: ProgramActivityCardProps) {
  const status = getProgramActivityStatus(activity);
  const timeRange = getProgramActivityTimeRange(activity);
  const statusText = {
    ended: text.ended,
    in_progress: text.inProgress,
    not_started: text.notStarted,
  }[status];
  const statusTextClassName = {
    ended: 'text-system-white48',
    in_progress: 'text-[#0086FF]',
    not_started: 'text-system-white',
  }[status];

  return (
    <article className="group flex min-h-[403px] flex-col rounded-[22px] bg-[#1d1e20]/95 p-6 shadow-[0_28px_80px_rgba(0,0,0,0.28)] transition duration-300 hover:bg-[#222326] sm:min-h-0 sm:p-4">
      <div className="relative aspect-[2.58/1] overflow-hidden rounded-[13px] border border-system-white12 bg-[#171b25]">
        {activity.cover_uri ? (
          <img
            src={activity.cover_uri}
            alt={title || text.coverAlt}
            className="size-full object-cover transition duration-500 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-[linear-gradient(135deg,#17191f,#08090c_58%,#1f2229)] text-sm text-white/44">
            {title || text.coverFallback}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col pt-5">
        <div className="flex items-center justify-between gap-4">
          <Link href={detailHref} className="min-w-0 transition hover:text-white/82">
            <h3 className="line-clamp-1 font-semibold text-[21px] text-white leading-snug sm:text-lg">{title}</h3>
          </Link>
          <span
            className={`shrink-0 rounded-full bg-white/[0.08] px-3 py-1 font-medium text-base leading-6 sm:text-sm ${statusTextClassName}`}
          >
            {statusText}
          </span>
        </div>
        <div className="mt-4 flex items-center gap-3 text-[16px] text-white/88 leading-6 sm:text-sm">
          <span className="size-4 shrink-0 rounded-full border-2 border-[#1396ff]" />
          <span>{timeRange ? `${text.eventTime} : ${timeRange}` : text.timePending}</span>
        </div>
        {actions && <div className="mt-auto pt-6">{actions}</div>}
      </div>
    </article>
  );
}
